import * as THREE from 'three';
import type { SketchLoopMeta, SketchVec3 } from './types';
import { planeBasis } from './triangulate';

/**
 * Exact curve generation.
 *
 * The Circle / Polygon / Arc tools produce a polyline boundary PLUS its
 * analytic definition (SketchLoopMeta). Push/Pull regenerates the lifted
 * loop FROM the definition (exact center/radius/angles, fresh tessellation)
 * instead of copying base-loop points, and inference snaps to the true
 * center. Arcs are exact circular arcs solved from three points.
 */

const asTuple = (p: THREE.Vector3): SketchVec3 => [p.x, p.y, p.z];

/** Sample an exact circular loop (open: first point not repeated). */
export function circularLoop(
    center: THREE.Vector3,
    radius: number,
    normal: THREE.Vector3,
    startAngle: number,
    endAngle: number,
    segments: number
): THREE.Vector3[] {
    const { u, v } = planeBasis(normal);
    const out: THREE.Vector3[] = [];
    for (let i = 0; i < segments; i++) {
        const angle = startAngle + (i / segments) * (endAngle - startAngle);
        out.push(
            center.clone()
                .addScaledVector(u, Math.cos(angle) * radius)
                .addScaledVector(v, Math.sin(angle) * radius)
        );
    }
    return out;
}

/** Full circle meta + loop (CCW, segments given). */
export function circleLoopExact(
    center: THREE.Vector3,
    radius: number,
    normal: THREE.Vector3,
    segments = 24
): { loop: THREE.Vector3[]; meta: SketchLoopMeta } {
    return {
        loop: circularLoop(center, radius, normal, 0, Math.PI * 2, segments),
        meta: {
            kind: 'circle',
            center: asTuple(center),
            normal: asTuple(normal.clone().normalize()),
            radius,
            startAngle: 0,
            endAngle: Math.PI * 2,
            segments,
        },
    };
}

/** Regular polygon meta + loop (vertex on the u axis of the plane basis). */
export function polygonLoopExact(
    center: THREE.Vector3,
    radius: number,
    normal: THREE.Vector3,
    sides: number
): { loop: THREE.Vector3[]; meta: SketchLoopMeta } {
    return {
        loop: circularLoop(center, radius, normal, 0, Math.PI * 2, sides),
        meta: {
            kind: 'polygon',
            center: asTuple(center),
            normal: asTuple(normal.clone().normalize()),
            radius,
            sides,
            startAngle: 0,
            endAngle: Math.PI * 2,
        },
    };
}

/**
 * Exact circular arc through three points (start, bulge ON the arc, end).
 * Solves the circumcircle in the start/end/bulge plane; the arc runs from
 * start to end passing through the bulge direction.
 */
export function arcLoopExact(
    start: THREE.Vector3,
    through: THREE.Vector3,
    end: THREE.Vector3,
    segments = 16
): { loop: THREE.Vector3[]; meta: SketchLoopMeta } | null {
    // Circumcenter of the three points: solve center = start + α·ab + β·ac
    // from the two perpendicular-bisector equations (Cramer's rule).
    const ab = through.clone().sub(start);
    const ac = end.clone().sub(start);
    const abab = ab.dot(ab);
    const abac = ab.dot(ac);
    const acac = ac.dot(ac);
    const d2 = 2 * (abab * acac - abac * abac);
    if (Math.abs(d2) < 1e-12) return null; // collinear points
    const alpha = (acac * (abab - abac)) / d2;
    const beta = (abab * (acac - abac)) / d2;
    const center = start.clone().addScaledVector(ab, alpha).addScaledVector(ac, beta);
    const radius = start.distanceTo(center);
    if (radius < 1e-9) return null;
    const normal = new THREE.Vector3().crossVectors(ab, ac).normalize();

    const { u, v } = planeBasis(normal);
    const angleOf = (p: THREE.Vector3): number => {
        const d = p.clone().sub(center);
        return Math.atan2(d.dot(v), d.dot(u));
    };
    let a0 = angleOf(start);
    const aMid = angleOf(through);
    let a1 = angleOf(end);
    // Normalize so the sweep start→end passes through the bulge angle.
    const norm = (a: number): number => (a < 0 ? a + Math.PI * 2 : a);
    const span1 = norm(a1 - a0);
    const spanMid = norm(aMid - a0);
    if (spanMid > span1) {
        // The direct sweep misses the bulge: go the other way around.
        a0 = norm(a0);
        a1 = norm(a1);
        if (a1 > a0) a1 -= Math.PI * 2;
    } else {
        a0 = norm(a0);
        a1 = norm(a1);
        if (a1 < a0) a1 += Math.PI * 2;
    }
    const loop = circularLoop(center, radius, normal, a0, a1, segments);
    return {
        loop,
        meta: {
            kind: 'arc',
            center: asTuple(center),
            normal: asTuple(normal),
            radius,
            startAngle: a0,
            endAngle: a1,
            segments,
        },
    };
}

/**
 * Regenerate a lifted loop from loop meta (Push/Pull fast path): exact
 * center/radius re-sampled in place of copying base tessellation points.
 */
export function regenerateLoop(meta: SketchLoopMeta, lift: (p: SketchVec3) => SketchVec3): SketchVec3[] {
    const center = new THREE.Vector3(...meta.center);
    const normal = new THREE.Vector3(...meta.normal);
    const segments = meta.kind === 'polygon'
        ? (meta.sides ?? 6)
        : (meta.segments ?? 24);
    const startAngle = meta.startAngle ?? 0;
    const endAngle = meta.endAngle ?? Math.PI * 2;
    const sampled = circularLoop(center, meta.radius, normal, startAngle, endAngle, segments);
    // Keep the caller's placement transform (loop is lifted into its plane).
    return sampled.map((p) => lift(asTuple(p)));
}
